import moment from 'moment';

import { getCurrentLanguage } from '../i18n';

/** Formatea una fecha con el formato indicado usando el idioma actual (zona horaria local). */
export function toDate(date: Date | moment.Moment, format: string): string {
  if (!date) {
    return '';
  }

  if (moment.isMoment(date)) {
    const momentDate = date as moment.Moment;
    return momentDate.locale(getCurrentLanguage()).format(format);
  }

  return moment(date).locale(getCurrentLanguage()).format(format);
}

/** Formatea una fecha con el formato indicado usando el idioma actual, convirtiéndola a UTC (sin zona horaria local). */
export function toDateWithoutZones(date: Date | moment.Moment, format: string): string {
  if (!date) {
    return '';
  }

  if (moment.isMoment(date)) {
    const momentDate = date as moment.Moment;
    return momentDate.locale(getCurrentLanguage()).utc().format(format);
  }

  return moment.utc(date).locale(getCurrentLanguage()).format(format);
}

/** Devuelve la fecha en formato corto localizado. */
export function toShortDate(date: Date | moment.Moment, withoutZones = false): string {
  if (withoutZones) {
    return toDateWithoutZones(date, 'L');  // 06/09/2014
  }

  return toDate(date, 'L');  // 06/09/2014
}

/** Devuelve la fecha y la hora en formato corto localizado. */
export function toShortDateWithHour(date: Date | moment.Moment, withoutZones = false): string {
  if (withoutZones) {
   return toDateWithoutZones(date, 'L') + ' ' + toDateWithoutZones(date, 'LT');  // 06/09/2014 12:19
  }

  return toDate(date, 'L') + ' ' + toDate(date, 'LT');  // 06/09/2014 12:19
}

/** Devuelve la fecha en formato largo localizado. */
export function toLongDate(date: Date | moment.Moment, withoutZones = false): string {
  if (withoutZones) {
    return toDateWithoutZones(date, 'LL'); // June 9 2014
  }

  return toDate(date, 'LL'); // June 9 2014
}

/** Devuelve la fecha y la hora en formato largo abreviado localizado. */
export function toLongLocalDateWithHour(date: Date | moment.Moment, withoutZones = false): string {
  if (withoutZones) {
    return toDateWithoutZones(date, 'lll');
  }

  return toDate(date, 'lll');
}

/** Devuelve la fecha y hora actuales. */
function today(): Date {
  return new Date();
}

/** Devuelve el primer día (lunes) de la semana actual. */
function weekStart(): Date {
  const current = today();
  const first = current.getDate() - current.getDay() + 1;
  return new Date(current.setDate(first));
}

/** Devuelve el día 6 días después de hoy (fin de semana calculado desde hoy). */
function weekEnd(): Date {
  const current = today();
  return new Date(current.setDate(current.getDate() + 6));
}

/** Devuelve el primer día del mes actual. */
function monthStart(): Date {
  return new Date(new Date().setDate(1));
}

/** Devuelve el último día del mes actual. */
function monthEnd(): Date {
  return new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
}

/** Devuelve el primer día del mes anterior. */
function lastMonthStart(): Date {
  const previousMonth = new Date().setMonth(new Date().getMonth() - 1);
  return new Date(new Date(previousMonth).setDate(1));
}

/** Devuelve el último día del mes anterior. */
function lastMonthEnd(): Date {
  const previousMonth = new Date().getMonth() - 1;
  const previousMonthDay = new Date().setMonth(previousMonth);
  const yearOfPreviousMonth = new Date(previousMonthDay).getFullYear();
  return new Date(yearOfPreviousMonth, previousMonth + 1, 0);
}

/** Devuelve el 1 de enero del año anterior. */
function lastYearStart(): Date {
  const previousYear = new Date().getFullYear() - 1;
  return new Date(previousYear, 0, 1);
}

/** Devuelve el 31 de diciembre del año anterior. */
function lastYearEnd(): Date {
  const previousYear = lastYearStart().getFullYear();
  const lastMonth = 11;
  return new Date(previousYear, lastMonth + 1, 0);
}

/** Devuelve la fecha de dentro de 7 días. */
function next7days(): Date {
  const current = new Date(today());
  current.setDate(current.getDate() + 7);
  return current;
}

/** Devuelve la fecha de dentro de 30 días. */
function next30days(): Date {
  const current = new Date(today());
  current.setDate(current.getDate() + 30);
  return current;
}

/** Devuelve la fecha de dentro de 60 días. */
function next60days(): Date {
  const current = new Date(today());
  current.setDate(current.getDate() + 60);
  return current;
}

/** Devuelve la fecha de hace 7 días. */
function last7days(): Date {
  const current = new Date(today());
  current.setDate(current.getDate() - 7);
  return current;
}

/** Devuelve la fecha de hace 30 días. */
function last30days(): Date {
  const current = new Date(today());
  current.setDate(current.getDate() - 30);
  return current;
}

/** Devuelve la fecha de hace 60 días. */
function last60days(): Date {
  const current = new Date(today());
  current.setDate(current.getDate() - 60);
  return current;
}

/** Clona una fecha conservando solo año, mes y día (hora a 00:00:00). */
export function cloneDate(date: Date): Date {
  if (!date) {
    return;
  }

  const newDate = new Date(date);

  return new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
}

/** Clona una fecha conservando fecha y hora completas, incluidos milisegundos. */
export function cloneDateTime(date: Date): Date {
  if (!date) {
    return;
  }

  const newDate = new Date(date);

  return new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), newDate.getHours(), newDate.getMinutes(), newDate.getSeconds(), newDate.getMilliseconds());
}

/** Obtiene el patrón de formato de fecha larga de moment para el idioma actual. */
export function getLongDateFormat(format: moment.LongDateFormatKey): string {
  if (!format) {
    return;
  }

  return moment.localeData(getCurrentLanguage()).longDateFormat(format);
}

/** Calcula la diferencia en milisegundos entre dos fechas (maxDate - minDate). */
export function getDifferenceInMilliseconds(maxDate: Date, minDate: Date): number {
  return moment(maxDate).diff(moment(minDate), 'milliseconds');
}

/** Calcula la diferencia en segundos entre dos fechas (maxDate - minDate). */
export function getDifferenceInSeconds(maxDate: Date, minDate: Date): number {
  return moment(maxDate).diff(moment(minDate), 'seconds');
}

/** Calcula la diferencia en minutos entre dos fechas (maxDate - minDate). */
export function getDifferenceInMinutes(maxDate: Date, minDate: Date): number {
  return moment(maxDate).diff(moment(minDate), 'minutes');
}

/** Calcula la diferencia en horas entre dos fechas (maxDate - minDate). */
export function getDifferenceInHours(maxDate: Date, minDate: Date): number {
  return moment(maxDate).diff(moment(minDate), 'hours');
}

/** Calcula la diferencia en días entre dos fechas (maxDate - minDate). */
export function getDifferenceInDays(maxDate: Date, minDate: Date): number {
  return moment(maxDate).diff(moment(minDate), 'days');
}

/** Objeto con funciones para obtener fechas relativas a hoy (semana, mes, año y desplazamientos en días). */
export const getDate = {
  today,
  weekStart,
  weekEnd,
  monthStart,
  monthEnd,
  lastMonthStart,
  lastMonthEnd,
  lastYearStart,
  lastYearEnd,
  next7days,
  next30days,
  next60days,
  last7days,
  last30days,
  last60days
};
